var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { krBaseCSS } from '../../style/base.js';
/**
 * A card-style radio group for selecting between a small number of options.
 * Each option renders as a bordered card with a radio indicator, label, and optional description.
 *
 * Uses ElementInternals for form association, allowing the component
 * to participate in form submission, validation, and reset.
 *
 * @element kr-radio-cards
 * @fires change - Fired when the selected value changes
 */
export class KRRadioCards extends LitElement {
    constructor() {
        super();
        // The value the user last picked. While dirty, an external `value` set is reverted to this (see
        // willUpdate) so a parent re-binding from the server can't overwrite an unsaved edit.
        this._dirtyValue = '';
        this._focusedIndex = -1;
        /**
         * The label text above the cards
         */
        this.label = '';
        /**
         * The input name for form submission
         */
        this.name = '';
        /**
         * The currently selected value
         */
        this.value = '';
        /**
         * The card options to display
         */
        this.options = [];
        /**
         * Whether the field is disabled
         */
        this.disabled = false;
        /**
         * Whether the field is required
         */
        this.required = false;
        /**
         * Helper text shown below the cards
         */
        this.hint = '';
        /**
         * Layout direction: 'row' or 'column'
         */
        this.direction = 'row';
        /**
         * Whether the user has changed this field since it was last loaded or marked pristine. Set when
         * the user picks a card, cleared by markPristine(). Used by <kr-form> to collect changed fields.
         */
        this.dirty = false;
        /**
         * Whether a <kr-form> save carrying this field's edit is in flight. Set by markSaving(), cleared
         * by markSaved()/markSaveFailed().
         */
        this.saving = false;
        this._touched = false;
        this._handleInvalid = (e) => {
            e.preventDefault();
            this._touched = true;
        };
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        // Marks this as a krubble form field so <kr-form> can find its controls (.kr-form-field).
        this.classList.add('kr-form-field');
        this.addEventListener('invalid', this._handleInvalid);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('invalid', this._handleInvalid);
    }
    firstUpdated() {
        this._updateValidity();
    }
    willUpdate(changedProperties) {
        // This field's own save-mode, else the enclosing <kr-form>'s. Unset either way means manual.
        let saveMode = this.saveMode;
        if (!saveMode) {
            saveMode = this.closest('kr-form')?.saveMode;
        }
        // While the user has an unsaved edit, an external `value` set (e.g. a parent re-binding from a
        // server response that predates the edit) would overwrite it. Revert to the user's value. The
        // field accepts external values again once the edit is saved (markSaving clears dirty) or reset.
        if (changedProperties.has('value') && this.dirty && this.value !== this._dirtyValue && saveMode === 'autosave') {
            this.value = this._dirtyValue;
        }
    }
    updated(changedProperties) {
        if (changedProperties.has('required') || changedProperties.has('value')) {
            this._updateValidity();
        }
    }
    // Form-associated custom element callbacks
    get form() {
        return this._internals.form;
    }
    get validity() {
        return this._internals.validity;
    }
    get validationMessage() {
        return this._internals.validationMessage;
    }
    get willValidate() {
        return this._internals.willValidate;
    }
    checkValidity() {
        return this._internals.checkValidity();
    }
    reportValidity() {
        return this._internals.reportValidity();
    }
    formResetCallback() {
        this.value = '';
        this._touched = false;
        this.dirty = false;
        this.saving = false;
        this._internals.setFormValue('');
        this._internals.setValidity({});
    }
    // Clears the dirty flag so the field is no longer reported as changed.
    markPristine() {
        this.dirty = false;
    }
    // <kr-form> calls this when a save carrying this field's edit starts. Clearing dirty here means a
    // further edit during the request is tracked as new and goes out in the next save.
    markSaving() {
        this.saving = true;
        this.dirty = false;
    }
    // <kr-form> calls this when the save succeeded. If the user edited mid-flight the field is dirty
    // again and stays that way.
    markSaved() {
        this.saving = false;
    }
    // <kr-form> calls this when the save failed. Restore dirty so the next change retries the edit.
    markSaveFailed() {
        this.saving = false;
        this.dirty = true;
        this._dirtyValue = this.value;
    }
    formStateRestoreCallback(state) {
        this.value = state;
    }
    _updateValidity() {
        if (this.required && !this.value) {
            this._internals.setValidity({ valueMissing: true }, 'Please select an option');
        }
        else {
            this._internals.setValidity({});
        }
    }
    _handleCardClick(e, option) {
        if (this.disabled || option.disabled) {
            return;
        }
        this.value = option.value;
        this._touched = true;
        this.dirty = true;
        this._dirtyValue = this.value;
        this._internals.setFormValue(this.value);
        this._updateValidity();
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true
        }));
    }
    _handleKeyDown(e, index) {
        if (this.disabled) {
            return;
        }
        if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            if (!this.options[index].disabled) {
                this._handleCardClick(e, this.options[index]);
            }
            return;
        }
        let nextIndex = -1;
        if (this.direction === 'row') {
            if (e.key === 'ArrowRight') {
                nextIndex = this._findNextEnabledIndex(index, 1);
            }
            else if (e.key === 'ArrowLeft') {
                nextIndex = this._findNextEnabledIndex(index, -1);
            }
        }
        else {
            if (e.key === 'ArrowDown') {
                nextIndex = this._findNextEnabledIndex(index, 1);
            }
            else if (e.key === 'ArrowUp') {
                nextIndex = this._findNextEnabledIndex(index, -1);
            }
        }
        if (nextIndex >= 0) {
            e.preventDefault();
            this._focusedIndex = nextIndex;
            const cards = this.shadowRoot?.querySelectorAll('.card');
            if (cards[nextIndex]) {
                cards[nextIndex].focus();
            }
        }
    }
    _findNextEnabledIndex(current, step) {
        let next = current + step;
        while (next >= 0 && next < this.options.length) {
            if (!this.options[next].disabled) {
                return next;
            }
            next += step;
        }
        return -1;
    }
    render() {
        return html `
      <div class="wrapper">
        ${this.label
            ? html `
            <label>
              ${this.label}
              ${this.required ? html `<span class="required" aria-hidden="true">*</span>` : nothing}
            </label>
          `
            : nothing}
        <div
          class=${classMap({
            'cards': true,
            'cards--row': this.direction === 'row',
            'cards--column': this.direction === 'column',
        })}
          role="radiogroup"
          aria-label=${this.label}
        >
          ${this.options.map((option, index) => {
            const isSelected = option.value === this.value;
            const isDisabled = this.disabled || !!option.disabled;
            return html `
              <div
                class=${classMap({
                'card': true,
                'card--selected': isSelected,
                'card--disabled': isDisabled,
            })}
                role="radio"
                aria-checked=${isSelected}
                aria-disabled=${isDisabled}
                tabindex=${isDisabled ? -1 : 0}
                @click=${(e) => this._handleCardClick(e, option)}
                @keydown=${(e) => this._handleKeyDown(e, index)}
              >
                <div class="card__radio">
                  ${isSelected ? html `<div class="card__radio-dot"></div>` : nothing}
                </div>
                <div class="card__content">
                  <div class="card__label">${option.label}</div>
                  ${option.description
                ? html `<div class="card__description">${option.description}</div>`
                : nothing}
                </div>
              </div>
            `;
        })}
        </div>
        ${this._touched && this.required && !this.value
            ? html `<div class="validation-message">Please select an option</div>`
            : this.hint
                ? html `<div class="hint">${this.hint}</div>`
                : nothing}
      </div>
    `;
    }
}
KRRadioCards.styles = [krBaseCSS, css `
    :host {
      display: block;
      font-family: inherit;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    label {
      font-size: 14px;
      font-weight: 500;
      color: black;
    }

    .required {
      color: var(--kr-radio-cards-required-color, #ef4444);
      margin-left: 0.125rem;
    }

    .cards {
      display: flex;
      gap: 12px;
    }

    .cards--row {
      flex-direction: row;
    }

    .cards--column {
      flex-direction: column;
    }

    .card {
      flex: 1;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 16px;
      border: 1px solid var(--kr-radio-cards-border-color, #0000003d);
      border-radius: 8px;
      background-color: var(--kr-radio-cards-bg, #fff);
      cursor: pointer;
      transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
    }

    .card:hover:not(.card--disabled) {
      border-color: var(--kr-radio-cards-hover-border-color, #9ca3af);
    }

    .card:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(22, 48, 82, 0.1);
    }

    .card--selected {
      border-color: var(--kr-radio-cards-selected-border-color, var(--kr-primary));
      background-color: var(--kr-radio-cards-selected-bg, rgba(22, 48, 82, 0.06));
    }

    .card--selected:hover {
      border-color: var(--kr-radio-cards-selected-border-color, var(--kr-primary));
    }

    .card--disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .card__radio {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      border: 2px solid var(--kr-radio-cards-radio-border-color, #0000003d);
      border-radius: 50%;
      margin-top: 1px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: border-color 0.2s;
    }

    .card--selected .card__radio {
      border-color: var(--kr-radio-cards-radio-selected-color, var(--kr-primary));
    }

    .card__radio-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: var(--kr-radio-cards-radio-selected-color, var(--kr-primary));
    }

    .card__content {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
    }

    .card__label {
      font-size: 14px;
      font-weight: 600;
      color: var(--kr-radio-cards-label-color, #000);
    }

    .card__description {
      font-size: 14px;
      color: var(--kr-radio-cards-description-color, #4d525c);
    }

    .hint {
      font-size: 0.75rem;
      color: var(--kr-radio-cards-helper-color, #6b7280);
    }

    .validation-message {
      font-size: 0.75rem;
      color: var(--kr-radio-cards-error-color, #ef4444);
    }
  `];
KRRadioCards.formAssociated = true;
__decorate([
    property({ type: String })
], KRRadioCards.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRRadioCards.prototype, "name", void 0);
__decorate([
    property({ type: String })
], KRRadioCards.prototype, "value", void 0);
__decorate([
    property({ attribute: false })
], KRRadioCards.prototype, "options", void 0);
__decorate([
    property({ type: Boolean })
], KRRadioCards.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], KRRadioCards.prototype, "required", void 0);
__decorate([
    property({ type: String })
], KRRadioCards.prototype, "hint", void 0);
__decorate([
    property({ type: String })
], KRRadioCards.prototype, "direction", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRRadioCards.prototype, "dirty", void 0);
__decorate([
    property({ attribute: 'save-mode' })
], KRRadioCards.prototype, "saveMode", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRRadioCards.prototype, "saving", void 0);
__decorate([
    state()
], KRRadioCards.prototype, "_touched", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-radio-cards')) {
    customElements.define('kr-radio-cards', KRRadioCards);
}
//# sourceMappingURL=radio-cards.js.map